// The command path: the contract's Connect services on the same origin
// the app is served from (the server mounts them beside the session
// socket, server/cmd/server/main.go), each call carrying the pages
// token as Bearer — what the server's interceptor checks
// (server/internal/auth/connect.go). One transport for every service;
// each slice takes its client off it.
import { createClient, type Interceptor, type Transport } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { create } from "@bufbuild/protobuf";
import { AnswerPendingRequestSchema, GateService, PendingVia, type PendingAnswer } from "@/gen/psypen/v1/gate_pb";
import type { PendingAction } from "@/gen/psypen/v1/session_pb";
import type { GateRpc } from "@/live/gate";
import { token } from "@/auth";

// The token is read per call, not once at boot: a call after a fresh
// sign-in carries the newest one.
const bearer: Interceptor = (next) => async (req) => {
  const t = token();
  if (t) req.header.set("Authorization", `Bearer ${t.access}`);
  return next(req);
};

let theTransport: Transport | null = null;

export function transport(): Transport {
  theTransport ??= createConnectTransport({
    baseUrl: location.origin,
    // Unary commands are tiny and never hot: JSON keeps what crosses
    // the wire legible, and the tests stub it without a proto codec.
    useBinaryFormat: false,
    interceptors: [bearer],
  });
  return theTransport;
}

// The gate's command path: AnswerPending, the Connect twin of POST
// /pending. The id is what the box showed, so the answer applies only
// if that is what waits now.
export function gateRpc(): GateRpc {
  const client = createClient(GateService, transport());
  return {
    async answer(id: string, answer: PendingAnswer): Promise<{ next?: PendingAction | undefined }> {
      const res = await client.answerPending(
        create(AnswerPendingRequestSchema, { id, answer, via: PendingVia.UNSPECIFIED }),
      );
      return { next: res.next };
    },
  };
}
